/**
 * Public Chat Storage
 *
 * Utilities for managing session-only chat persistence for public (unauthenticated) users
 *
 * Primary responsibilities:
 * - Store chat messages in sessionStorage
 * - Load chat messages from sessionStorage
 * - Clear session chats on logout/navigation
 */

import { ChatMessage } from '@/types'

const SESSION_STORAGE_PREFIX = 'public_chat_'

/**
 * Save chat messages to sessionStorage
 */
export function saveChatToSession(chatId: string, messages: ChatMessage[]): void {
  if (typeof window === 'undefined') return

  try {
    const key = `${SESSION_STORAGE_PREFIX}${chatId}`
    sessionStorage.setItem(key, JSON.stringify(messages))
  } catch (error) {
    console.error('Failed to save chat to sessionStorage:', error)
  }
}

/**
 * Load chat messages from sessionStorage
 */
export function loadChatFromSession(chatId: string): ChatMessage[] | null {
  if (typeof window === 'undefined') return null

  try {
    const key = `${SESSION_STORAGE_PREFIX}${chatId}`
    const data = sessionStorage.getItem(key)
    if (!data) return null

    return JSON.parse(data) as ChatMessage[]
  } catch (error) {
    console.error('Failed to load chat from sessionStorage:', error)
    return null
  }
}

/**
 * Clear all session chats
 */
export function clearSessionChats(): void {
  if (typeof window === 'undefined') return

  try {
    const keys = Object.keys(sessionStorage)
    keys.forEach((key) => {
      if (key.startsWith(SESSION_STORAGE_PREFIX)) {
        sessionStorage.removeItem(key)
      }
    })
  } catch (error) {
    console.error('Failed to clear session chats:', error)
  }
}

/**
 * Clear a specific chat from sessionStorage
 */
export function clearChatFromSession(chatId: string): void {
  if (typeof window === 'undefined') return

  try {
    const key = `${SESSION_STORAGE_PREFIX}${chatId}`
    sessionStorage.removeItem(key)
  } catch (error) {
    console.error('Failed to clear chat from sessionStorage:', error)
  }
}

/**
 * Get all session chat IDs
 */
export function getSessionChatIds(): string[] {
  if (typeof window === 'undefined') return []

  try {
    const keys = Object.keys(sessionStorage)
    return keys
      .filter((key) => key.startsWith(SESSION_STORAGE_PREFIX))
      .map((key) => key.replace(SESSION_STORAGE_PREFIX, ''))
  } catch (error) {
    console.error('Failed to get session chat IDs:', error)
    return []
  }
}
